import { z } from 'zod';
import { Dateish } from './common';

export const AutomationTriggerSchema = z.enum([
  'ticket_created',
  'stage_entered',
  'stage_exited',
  'comment_added',
  'ticket_closed',
  'stagnant_in_stage',
]);
export type AutomationTrigger = z.infer<typeof AutomationTriggerSchema>;

/** Condições (todas opcionais = automação vale para qualquer ticket da mesa). */
export const AutomationConditionsSchema = z.object({
  stageId: z.number().int().positive().optional(),
  priorityIds: z.array(z.number().int().positive()).optional(),
  clientIds: z.array(z.number().int().positive()).optional(),
  /** obrigatório quando trigger = stagnant_in_stage (minutos CORRIDOS) */
  stagnantMinutes: z.number().int().positive().optional(),
});
export type AutomationConditions = z.infer<typeof AutomationConditionsSchema>;

const EmailRecipientSchema = z.enum(['requestor', 'responsible', 'custom']);

export const AutomationActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('send_email'),
    templateId: z.number().int().positive(),
    to: z.array(EmailRecipientSchema).min(1),
    customEmails: z.array(z.string().email()).optional(),
  }),
  z.object({ type: z.literal('set_stage'), stageId: z.number().int().positive() }),
  z.object({ type: z.literal('set_status'), statusId: z.number().int().positive() }),
  z.object({ type: z.literal('set_priority'), priorityId: z.number().int().positive() }),
  /** userId null = remover responsável */
  z.object({ type: z.literal('set_responsible'), userId: z.number().int().positive().nullable() }),
  z.object({ type: z.literal('add_comment'), body: z.string().min(1), internal: z.boolean() }),
]);
export type AutomationAction = z.infer<typeof AutomationActionSchema>;

export const AutomationSchema = z.object({
  id: z.number().int(),
  /** null = automação GLOBAL do tenant ("Todas" as mesas) */
  deskId: z.number().int().nullable(),
  name: z.string(),
  active: z.boolean(),
  trigger: AutomationTriggerSchema,
  conditions: AutomationConditionsSchema,
  actions: z.array(AutomationActionSchema),
  position: z.number().int(),
  createdAt: Dateish,
});
export type Automation = z.infer<typeof AutomationSchema>;

/** Item de GET /automations — deskName null = 'Todas' (automação global). */
export const AutomationListItemSchema = AutomationSchema.extend({
  deskName: z.string().nullable(),
});
export type AutomationListItem = z.infer<typeof AutomationListItemSchema>;

/** GET /automations?desk_id — filtro opcional por mesa. */
export const AutomationListQuerySchema = z.object({
  desk_id: z.coerce.number().int().positive().optional(),
});
export type AutomationListQuery = z.infer<typeof AutomationListQuerySchema>;

/**
 * Corpo PURO (sem deskId), strict: é o do POST aninhado
 * /desks/:deskId/automations, onde a mesa vem do PATH — um deskId no corpo é
 * rejeitado pela validação, não silenciosamente ignorado.
 */
const AutomationBodyObjectSchema = z
  .object({
    name: z.string().min(1),
    trigger: AutomationTriggerSchema,
    conditions: AutomationConditionsSchema.default({}),
    actions: z.array(AutomationActionSchema),
    active: z.boolean().optional(),
  })
  .strict();

const stagnantMinutesRule = (
  body: { trigger: AutomationTrigger; conditions: AutomationConditions },
  ctx: z.RefinementCtx
): void => {
  if (body.trigger === 'stagnant_in_stage' && body.conditions.stagnantMinutes === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['conditions', 'stagnantMinutes'],
      message: 'required when trigger is stagnant_in_stage',
    });
  }
};

export const AutomationBodySchema = AutomationBodyObjectSchema.superRefine(stagnantMinutesRule);
export type AutomationBody = z.infer<typeof AutomationBodySchema>;

/**
 * POST /automations: deskId OBRIGATÓRIO no corpo — número = mesa, null =
 * automação GLOBAL ('Todas'). Nullable ≠ optional: omitir é erro de validação,
 * global é escolha EXPLÍCITA.
 */
export const AutomationCreateBodySchema = AutomationBodyObjectSchema.extend({
  deskId: z.number().int().positive().nullable(),
}).superRefine(stagnantMinutesRule);
export type AutomationCreateBody = z.infer<typeof AutomationCreateBodySchema>;

/**
 * PUT /automations/:id: deskId OPCIONAL — ausente mantém o escopo atual;
 * número|null move a automação de mesa (null = vira global).
 */
export const AutomationUpdateBodySchema = AutomationBodyObjectSchema.extend({
  deskId: z.number().int().positive().nullable().optional(),
}).superRefine(stagnantMinutesRule);
export type AutomationUpdateBody = z.infer<typeof AutomationUpdateBodySchema>;

/**
 * `skipped` (F-178): a automação poria como alvo alguém desligado neste tenant e
 * não agiu — nem falhou, nem aplicou. O motivo vai em `error`
 * (`action[i] set_responsible: skipped_inactive_target`).
 */
export const AutomationRunStatusSchema = z.enum(['ok', 'failed', 'skipped']);
export type AutomationRunStatus = z.infer<typeof AutomationRunStatusSchema>;

export const AutomationRunSchema = z.object({
  id: z.number().int(),
  automationId: z.number().int(),
  ticketNumber: z.number().int(),
  status: AutomationRunStatusSchema,
  error: z.string().nullable(),
  createdAt: Dateish,
});
export type AutomationRun = z.infer<typeof AutomationRunSchema>;

/** GET /automations/runs?desk_id&automation_id */
export const AutomationRunsQuerySchema = z.object({
  desk_id: z.coerce.number().int().positive().optional(),
  automation_id: z.coerce.number().int().positive().optional(),
});
export type AutomationRunsQuery = z.infer<typeof AutomationRunsQuerySchema>;
